import type { AppLanguage } from './types';
import type { GeneralInfoPicklistItem } from '../api/generalInfoApi';

/** Normalize API / UI labels for lookup (English aliases, punctuation). */
export function normalizeGeoLabelKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\s+district$/i, '')
    .replace(/\s+area$/i, '')
    .replace(/\s+region$/i, '')
    .replace(/^mehoz\s+/i, '')
    .replace(/^the\s+/i, '');
}

/**
 * Israeli administrative districts (mehozot) and common sub-district (nafa) names.
 * Keys are normalized English (and common API variants).
 */
const HEBREW_BY_KEY: Record<string, string> = {
  // Country
  israel: 'ישראל',

  // Six mehozot + aliases
  jerusalem: 'ירושלים',
  yerushalayim: 'ירושלים',
  'jerusalem district': 'מחוז ירושלים',

  northern: 'צפון',
  north: 'צפון',
  hazafon: 'צפון',
  haatzafon: 'צפון',
  'northern district': 'מחוז הצפון',
  'north district': 'מחוז הצפון',

  haifa: 'חיפה',
  heifa: 'חיפה',
  hefa: 'חיפה',
  'haifa district': 'מחוז חיפה',

  central: 'מרכז',
  hamerkaz: 'מרכז',
  'central district': 'מחוז המרכז',

  'tel aviv': 'תל אביב',
  telaviv: 'תל אביב',
  'tel-aviv': 'תל אביב',
  'tel aviv district': 'מחוז תל אביב',
  'tel aviv-yafo': 'תל אביב-יפו',
  'tel aviv yafo': 'תל אביב-יפו',

  southern: 'דרום',
  south: 'דרום',
  hadarom: 'דרום',
  'southern district': 'מחוז הדרום',
  'south district': 'מחוז הדרום',

  'judea and samaria': 'יהודה ושומרון',
  'judea & samaria': 'יהודה ושומרון',
  'judaea and samaria': 'יהודה ושומרון',
  'judea and samaria area': 'אזור יהודה ושומרון',
  'judaea and samaria area': 'אזור יהודה ושומרון',
  'west bank': 'יהודה ושומרון',

  // Common sub-districts (nafot)
  acre: 'עכו',
  akko: 'עכו',
  acco: 'עכו',
  golan: 'גולן',
  galilee: 'גליל',
  'lower galilee': 'גליל תחתון',
  'upper galilee': 'גליל עליון',
  kinneret: 'כנרת',
  kineret: 'כנרת',
  'kinneret sub-district': 'כנרת',
  zefat: 'צפת',
  safed: 'צפת',
  tiberias: 'טבריה',
  hadera: 'חדרה',
  sharon: 'שרון',
  hasharon: 'השרון',
  'petah tikva': 'פתח תקווה',
  'petah tikvah': 'פתח תקוה',
  'petach tikva': 'פתח תקווה',
  ramla: 'רמלה',
  'ramla subdistrict': 'רמלה',
  rehovot: 'רחובות',
  hashfela: 'השפלה',
  'hefer valley': 'עמק חפר',
  ashkelon: 'אשקלון',
  ashqelon: 'אשקלון',
  beersheba: 'באר שבע',
  'beer sheva': 'באר שבע',
  'be er sheva': 'באר שבע',
  beersheva: 'באר שבע',
  'beer sheva sub-district': 'באר שבע',
  jaffa: 'יפו',
  yafo: 'יפו',
  modiin: 'מודיעין',
  'modiin-maccabim-reut': 'מודיעין-מכבים-רעות',
  nazareth: 'נצרת',
  'nof hagalil': 'נוף הגליל',
  'upper nazareth': 'נוף הגליל',

  // Additional sub-districts (nafot) and common API labels
  yizrael: 'יזרעאל',
  jezreel: 'עמק יזרעאל',
  'jezreel valley': 'עמק יזרעאל',
  'emek yizrael': 'עמק יזרעאל',
  afula: 'עפולה',
  menashe: 'מנשה',
  megiddo: 'מגידו',
  'maale yosef': 'מעלה יוסף',
  'maaleh yosef': 'מעלה יוסף',
  qatzrin: 'קצרין',
  'katzrin sub-district': 'קצרין',
  'jordan valley': 'בקעת הירדן',
  'emek hayarden': 'עמק הירדן',
  arava: 'ערבה',
  'central arava': 'ערבה מרכזית',
  eilat: 'אילת',
  dimona: 'דימונה',
  netanya: 'נתניה',
  herzliya: 'הרצליה',
  holon: 'חולון',
  'bat yam': 'בת ים',
  'rishon lezion': 'ראשון לציון',
  'rishon le zion': 'ראשון לציון',
  'bnei brak': 'בני ברק',
  'kfar saba': 'כפר סבא',
  'kfar-saba': 'כפר סבא',
  raanana: 'רעננה',
  lod: 'לוד',
  lydda: 'לוד',
  'ramat gan': 'רמת גן',
  'gush dan': 'גוש דן',
  'dan region': 'גוש דן',
  'ashdod sub-district': 'אשדוד',
  ashdod: 'אשדוד',
  'beer sheva subdistrict': 'באר שבע',
  'beer sheba': 'באר שבע',
  'ramat negev': 'רמת נגב',
  'negev highlands': 'רמות נגב',
  'negev hills': 'רמות נגב',
  'tel aviv sub-district': 'תל אביב',
  'tel aviv subdistrict': 'תל אביב',
  'hasharon sub-district': 'השרון',
  'hashfela sub-district': 'השפלה',
  'hashfela subdistrict': 'השפלה',
  'petah tikva sub-district': 'פתח תקווה',
  'ramla sub-district': 'רמלה',
  'rehovot sub-district': 'רחובות',
  'hadera sub-district': 'חדרה',
  'haifa sub-district': 'חיפה',
  'zefat sub-district': 'צפת',
  'safed sub-district': 'צפת',
  'tiberias sub-district': 'טבריה',
  'acre sub-district': 'עכו',
  'akko sub-district': 'עכו',
  'golan sub-district': 'גולן',
  'lower galilee sub-district': 'גליל תחתון',
  'upper galilee sub-district': 'גליל עליון',
};

/** Whether an API region/district label corresponds to an official sub-district (nafa) name. */
export function israelSubDistrictMatchesRegionLabel(nafaName: string, regionLabel: string): boolean {
  const nafa = nafaName.trim();
  const label = regionLabel.trim();
  if (!nafa || !label) {
    return false;
  }
  if (label.includes(nafa) || nafa.includes(label)) {
    return true;
  }
  const heRegion = localizeGeoLabel(label, 'he').trim();
  if (heRegion === nafa || heRegion.includes(nafa) || nafa.includes(heRegion)) {
    return true;
  }
  const regionKey = normalizeGeoLabelKey(label)
    .replace(/\s+sub[- ]?district$/i, '')
    .replace(/\s+subdistrict$/i, '');
  for (const [key, he] of Object.entries(HEBREW_BY_KEY)) {
    if (he !== nafa && !he.includes(nafa) && !nafa.includes(he)) {
      continue;
    }
    const aliasKey = key.replace(/\s+sub[- ]?district$/i, '').replace(/\s+subdistrict$/i, '');
    if (regionKey.includes(aliasKey) || aliasKey.includes(regionKey)) {
      return true;
    }
  }
  return false;
}

function lookupHebrew(label: string): string | undefined {
  const raw = label.trim();
  if (!raw) {
    return undefined;
  }
  const primary = normalizeGeoLabelKey(raw);
  if (HEBREW_BY_KEY[primary]) {
    return HEBREW_BY_KEY[primary];
  }
  const withDistrict = normalizeGeoLabelKey(`${raw} district`);
  if (HEBREW_BY_KEY[withDistrict]) {
    return HEBREW_BY_KEY[withDistrict];
  }
  return HEBREW_BY_KEY[raw.toLowerCase()];
}

/** Display label for countries / regions / districts when UI language is Hebrew. */
export function localizeGeoLabel(label: string, language: AppLanguage): string {
  if (language !== 'he') {
    return label;
  }
  const trimmed = label.trim();
  if (!trimmed) {
    return label;
  }
  if (/[\u0590-\u05FF]/.test(trimmed)) {
    return trimmed;
  }
  return lookupHebrew(trimmed) ?? label;
}

export function localizePicklistItems(
  items: readonly GeneralInfoPicklistItem[],
  language: AppLanguage,
): GeneralInfoPicklistItem[] {
  if (language !== 'he') {
    return [...items];
  }
  return items.map((item) => ({
    ...item,
    label: localizeGeoLabel(item.label, language),
  }));
}
